import { last, zip } from "lodash";

import { ExtractBranchFromUnion } from "../discriminatedUnionUtils.js";
import { CalcScopeItemTypeSubset } from "../display-table/calcTypes.js";
import { DisplayTableColumnOutputType } from "../display-table/outputTypes.js";
import { assertNever, guardNever } from "../errors.js";
import {
  HqlBinaryOperator,
  isHqlAggregationFunctionName,
  isHqlNextOnlyFunctionName,
} from "../hql/types.js";
import { setEquals } from "../utils/setUtils.js";

import { CalcColumnContext, CalcParamContext } from "./CalcColumnContext.js";
import {
  CalcFunctionInfo,
  CalcParameterKind,
  CalcParameterType,
  getCalcFunctionInfo,
} from "./CalcFunctionDictionary.js";
import type { CalcParseNodeOrError, CalcParseResult } from "./CalcParser.js";

export enum BaseCalcType {
  NULL,
  STRING,
  NUMBER, // do we want to change this to have integer & float treated differently?
  DATETIME,
  TIME,
  BOOLEAN,
  UNKNOWN, // TODO(EXP-222): figure out if this should always be a warning or always be fine
}

export enum CalcKind {
  LITERAL_SCALAR,
  SCALAR,
  LITERAL_COLUMN,
  COLUMN,
  ANY,
}

export interface CalcType {
  type: BaseCalcType;
  kind: CalcKind;
  containsAggregate?: boolean;
}

// types that can be ordered using inequality operators
const HAS_ORDER: Record<BaseCalcType, boolean> = {
  [BaseCalcType.NULL]: false,
  [BaseCalcType.STRING]: true,
  [BaseCalcType.NUMBER]: true,
  [BaseCalcType.DATETIME]: true,
  [BaseCalcType.TIME]: true,
  [BaseCalcType.BOOLEAN]: false,
  [BaseCalcType.UNKNOWN]: false,
};

export function baseTypeToDisplayStr(baseType: BaseCalcType): string {
  switch (baseType) {
    case BaseCalcType.NULL:
      return "null";
    case BaseCalcType.STRING:
      return "text";
    case BaseCalcType.NUMBER:
      return "a number";
    case BaseCalcType.DATETIME:
      return "a datetime";
    case BaseCalcType.TIME:
      return "a time";
    case BaseCalcType.BOOLEAN:
      return "a boolean";
    case BaseCalcType.UNKNOWN:
      return "unknown";
    default:
      assertNever(baseType, baseType);
  }
}

function kindToDisplayStr(calcType: CalcType): string {
  const typStr = baseTypeToDisplayStr(calcType.type);
  switch (calcType.kind) {
    case CalcKind.LITERAL_SCALAR:
      return `${typStr} value`;
    case CalcKind.SCALAR:
      return `${typStr} expression`;
    case CalcKind.LITERAL_COLUMN:
    case CalcKind.COLUMN:
      return `${typStr} column`;
    case CalcKind.ANY:
      return "a value or column";
  }
}

// We might be able to merge these two types and/or need a more sophisticated system
// But this works for the MVP.
function calcParamTypeToCalcType(paramType: CalcParameterType): BaseCalcType {
  switch (paramType) {
    case "number":
      return BaseCalcType.NUMBER;
    case "boolean":
      return BaseCalcType.BOOLEAN;
    case "text":
      return BaseCalcType.STRING;
    case "datetime":
      return BaseCalcType.DATETIME;
    case "T":
      return BaseCalcType.UNKNOWN;
    case "U":
      return BaseCalcType.UNKNOWN;
    default:
      assertNever(paramType, paramType);
  }
}

function calcParamKindToCalcKind(paramKind: CalcParameterKind): CalcKind {
  switch (paramKind) {
    case "literal_scalar":
      return CalcKind.LITERAL_SCALAR;
    case "scalar":
      return CalcKind.SCALAR;
    case "literal_column":
      return CalcKind.LITERAL_COLUMN;
    case "column":
      return CalcKind.COLUMN;
    case "any":
      return CalcKind.ANY;
  }
}

function columnTypeToType(colType: DisplayTableColumnOutputType): CalcType {
  switch (colType) {
    case "STRING":
      return { type: BaseCalcType.STRING, kind: CalcKind.LITERAL_COLUMN };
    case "NUMBER":
      return { type: BaseCalcType.NUMBER, kind: CalcKind.LITERAL_COLUMN };
    // For type-checking purposes, we treat all date/datetime types as DATETIME
    // We can make this more specific in the future if needed.
    case "DATETIME":
    case "DATETIMETZ":
    case "DATE":
      return { type: BaseCalcType.DATETIME, kind: CalcKind.LITERAL_COLUMN };
    case "TIME":
      return { type: BaseCalcType.TIME, kind: CalcKind.LITERAL_COLUMN };
    case "UNKNOWN":
      return { type: BaseCalcType.UNKNOWN, kind: CalcKind.LITERAL_COLUMN };
    case "BOOLEAN":
      return { type: BaseCalcType.BOOLEAN, kind: CalcKind.LITERAL_COLUMN };
    default:
      assertNever(colType, colType);
  }
}

export function calcTypeToColumnType(
  calcType: BaseCalcType,
): DisplayTableColumnOutputType {
  switch (calcType) {
    case BaseCalcType.STRING:
      return "STRING";
    case BaseCalcType.NUMBER:
      return "NUMBER";
    case BaseCalcType.DATETIME:
      return "DATETIME";
    case BaseCalcType.TIME:
      return "TIME";
    case BaseCalcType.BOOLEAN:
      return "BOOLEAN";
    case BaseCalcType.UNKNOWN:
      return "UNKNOWN";
    case BaseCalcType.NULL:
      return "UNKNOWN";
    default:
      assertNever(calcType, calcType);
  }
}

function scopeTypeToType(scopeType: CalcScopeItemTypeSubset): CalcType {
  switch (scopeType) {
    case "STRING":
      return { type: BaseCalcType.STRING, kind: CalcKind.SCALAR };
    case "NUMBER":
      return { type: BaseCalcType.NUMBER, kind: CalcKind.SCALAR };
    case "BOOLEAN":
      return { type: BaseCalcType.BOOLEAN, kind: CalcKind.SCALAR };
    case "DATE":
    case "DATETIME":
      return { type: BaseCalcType.DATETIME, kind: CalcKind.SCALAR };
    default:
      guardNever(scopeType, scopeType);
      return { type: BaseCalcType.UNKNOWN, kind: CalcKind.SCALAR };
  }
}

interface CalcTypeError {
  message: string;
  selfStart: number;
  selfEnd: number;
}

export type CalcTypeCheckerAggregationMode =
  // e.g. pivot tables
  | "groupable"
  // e.g. display table
  | "ungroupable"
  | "disabled";

export class CalcTypeChecker {
  private root: CalcParseResult;
  private allColumnContext: ReadonlyMap<string, CalcType>;
  private paramContext: CalcParamContext;

  private hasBeenChecked = false;
  // undefined until type-checking has run, then acts as a cache of results
  private typeErrors: CalcTypeError[] | undefined;
  private rootType: CalcType | undefined;
  /**
   * Different (agg) calcs are allowed depending on if rows can be grouped.
   * For example, "A / Sum(A)" works for display tables because "Sum(A)"
   * is the same for every row. But  pivot tables, it doesn't make sense
   * to calculate "column / Sum(column)" for different (aggregated) groups of rows.
   */
  private aggregationMode: CalcTypeCheckerAggregationMode;
  private disableHqlNextOnlyFunctions: boolean;

  constructor(
    root: CalcParseResult,
    columnContext: CalcColumnContext,
    paramContext: CalcParamContext,
    otherCalcs: ReadonlyMap<string, CalcType>,
    aggregationMode: CalcTypeCheckerAggregationMode = "ungroupable",
    disableHqlNextOnlyFunctions?: boolean,
  ) {
    this.root = root;
    this.allColumnContext = new Map([
      ...columnContext.map(
        (c) =>
          [
            c.columnId,
            {
              ...columnTypeToType(c.columnType),
              containsAggregate: c.containsAggregate,
            },
          ] as const,
      ),
      ...[...otherCalcs.entries()].map(
        ([colId, type]) =>
          [colId, { ...type, kind: unifyKinds([type.kind]) }] as const,
      ),
    ]);
    this.paramContext = paramContext;
    this.aggregationMode = aggregationMode;
    this.disableHqlNextOnlyFunctions = disableHqlNextOnlyFunctions ?? true;
  }

  /**
   * Walks through `root` and returns a list of type errors it find in it or any descendants.
   */
  public getTypeErrors(): CalcTypeError[] {
    if (!this.hasBeenChecked || this.typeErrors == null) {
      this.hasBeenChecked = true;
      this.typeErrors = [];
      this.rootType = this.getTypeAndCheck(this.root);
    }

    return this.typeErrors;
  }

  public getRootType(): CalcType {
    if (!this.hasBeenChecked || this.rootType == null) {
      // This will run populate this.rootType
      this.getTypeErrors();
    }

    return this.rootType!;
  }

  private addTypeError(node: CalcParseNodeOrError, message: string): void {
    this.typeErrors ??= [];
    this.typeErrors.push({
      message,
      selfStart: node.start,
      selfEnd: node.end,
    });
  }

  // eslint-disable-next-line max-params -- it's fine
  private expectType(
    type: CalcType,
    expectedType: BaseCalcType,
    nodeForLocation: CalcParseNodeOrError,
    msg: string,
  ): boolean {
    // Check type
    if (
      type.type !== expectedType &&
      type.type !== BaseCalcType.UNKNOWN &&
      type.type !== BaseCalcType.NULL
    ) {
      this.addTypeError(nodeForLocation, msg);
      return false;
    }

    return true;
  }

  private expectKind(
    type: CalcType,
    expectedKind: CalcKind,
    nodeForLocation: CalcParseNodeOrError,
    msg: string,
  ): boolean {
    // Check kind
    if (!isSubKind(type.kind, expectedKind)) {
      this.addTypeError(nodeForLocation, msg);
      return false;
    }
    return true;
  }

  private getTypeAndCheck(node: CalcParseNodeOrError): CalcType {
    switch (node.type) {
      case "unaryOp":
        return this.getTypeAndCheckUnaryOp(node);
      case "binaryOp":
        return this.getTypeAndCheckBinaryOp(node);
      case "function":
        return this.getTypeAndCheckFunction(node);
      case "boolean":
        return { type: BaseCalcType.BOOLEAN, kind: CalcKind.LITERAL_SCALAR };
      case "float":
        return { type: BaseCalcType.NUMBER, kind: CalcKind.LITERAL_SCALAR };
      case "integer":
        return { type: BaseCalcType.NUMBER, kind: CalcKind.LITERAL_SCALAR };
      case "null":
        return { type: BaseCalcType.NULL, kind: CalcKind.LITERAL_SCALAR };
      case "str":
        return { type: BaseCalcType.STRING, kind: CalcKind.LITERAL_SCALAR };
      case "column":
        return (
          this.allColumnContext.get(node.name) ?? {
            type: BaseCalcType.UNKNOWN,
            kind: CalcKind.LITERAL_COLUMN,
            containsAggregate: false,
          }
        );
      case "parameterReference": {
        const maybeParamScopeType = this.paramContext[node.name];
        if (
          maybeParamScopeType != null &&
          // Avoid bugs that could happen with param names like `toString`.
          Object.hasOwn(this.paramContext, node.name)
        ) {
          return scopeTypeToType(maybeParamScopeType);
        }

        return {
          type: BaseCalcType.UNKNOWN,
          kind: CalcKind.SCALAR,
        };
      }
      case "error":
        return { type: BaseCalcType.UNKNOWN, kind: CalcKind.ANY };
      default:
        assertNever(node, node);
    }
  }

  private getTypeAndCheckUnaryOp(
    node: ExtractBranchFromUnion<CalcParseNodeOrError, "type", "unaryOp">,
  ): CalcType {
    switch (node.op) {
      case "MINUS": {
        const type = this.getTypeAndCheck(node.left);
        this.expectType(
          type,
          BaseCalcType.NUMBER,
          node,
          "Minus operator must be applied to a number or column of numbers",
        );
        return {
          type: BaseCalcType.NUMBER,
          kind: unifyKinds([type.kind]),
          containsAggregate: type.containsAggregate,
        };
      }
      case "LOGICALNOT": {
        const type = this.getTypeAndCheck(node.left);
        this.expectType(
          type,
          BaseCalcType.BOOLEAN,
          node,
          "Not operator must be applied to a boolean or column of booleans",
        );
        return {
          type: BaseCalcType.BOOLEAN,
          kind: unifyKinds([type.kind]),
          containsAggregate: type.containsAggregate,
        };
      }
      default:
        assertNever(node.op, node.op);
    }
  }

  private getTypeAndCheckBinaryOp(
    node: ExtractBranchFromUnion<CalcParseNodeOrError, "type", "binaryOp">,
  ): CalcType {
    const leftType = this.getTypeAndCheck(node.left);
    const rightType = this.getTypeAndCheck(node.right);
    const unifiedKind = unifyKinds([leftType.kind, rightType.kind]);
    const containsAggregate =
      leftType.containsAggregate || rightType.containsAggregate;
    if (this.aggregationMode === "groupable") {
      if (
        mixesAggFunctionAndReferencedAgg(
          node.left,
          leftType,
          node.right,
          rightType,
        )
      ) {
        this.addTypeError(
          node,
          `Sorry, we currently don't support binary operations on (A) an aggregate function and (B) an aggregate calculation or measure. Please try putting (A) in its own calculation`,
        );
      } else if (!isGroupableBinaryOpValid(leftType, rightType)) {
        this.addTypeError(
          node,
          `Can't ${hqlOpToVerb[node.op]} aggregated and unaggregated data`,
        );
      }
    }
    switch (node.op) {
      case "LOGICALOR":
      case "LOGICALAND": {
        this.expectType(
          leftType,
          BaseCalcType.BOOLEAN,
          node.left,
          "Left side of a logic operator must be a boolean or column of booleans",
        );
        this.expectType(
          rightType,
          BaseCalcType.BOOLEAN,
          node.right,
          "Right side of a logic operator must be a boolean or a column of booleans",
        );
        return {
          type: BaseCalcType.BOOLEAN,
          kind: unifiedKind,
          containsAggregate,
        };
      }
      case "PLUS":
      case "MINUS":
      case "MULTIPLY":
      case "DIVIDE":
      case "EXPONENT":
      case "MODULO": {
        this.expectType(
          leftType,
          BaseCalcType.NUMBER,
          node.left,
          "Left side of an arithmetic operator must be a number or column of numbers",
        );
        this.expectType(
          rightType,
          BaseCalcType.NUMBER,
          node.right,
          "Right side of an arithmetic operator must be a number or column of numbers",
        );
        return {
          type: BaseCalcType.NUMBER,
          kind: unifiedKind,
          containsAggregate,
        };
      }
      case "CONCAT": {
        this.expectType(
          leftType,
          BaseCalcType.STRING,
          node.left,
          "Left side of a concat operator must be text or a column of text",
        );
        this.expectType(
          rightType,
          BaseCalcType.STRING,
          node.right,
          "Right side of an concat operator must be text or a column of text",
        );
        return {
          type: BaseCalcType.STRING,
          kind: unifiedKind,
          containsAggregate,
        };
      }

      case "EQUAL":
      case "NOTEQUAL": {
        if (
          leftType.type === BaseCalcType.NULL ||
          rightType.type === BaseCalcType.NULL
        ) {
          return {
            type: BaseCalcType.BOOLEAN,
            kind: unifiedKind,
            containsAggregate,
          };
        }

        if (
          leftType.type !== rightType.type &&
          leftType.type !== BaseCalcType.UNKNOWN &&
          rightType.type !== BaseCalcType.UNKNOWN
        ) {
          this.addTypeError(
            node,
            `Cannot compare ${baseTypeToDisplayStr(
              leftType.type,
            )} to ${baseTypeToDisplayStr(rightType.type)}`,
          );
        }

        return {
          type: BaseCalcType.BOOLEAN,
          kind: unifiedKind,
          containsAggregate,
        };
      }
      case "GREATER":
      case "GREATEREQUAL":
      case "LESS":
      case "LESSEQUAL": {
        // TODO(EXP-221): more lenient rules for date/time stuff
        if (
          leftType.type !== rightType.type &&
          leftType.type !== BaseCalcType.UNKNOWN &&
          rightType.type !== BaseCalcType.UNKNOWN
        ) {
          this.addTypeError(
            node,
            `Cannot compare ${baseTypeToDisplayStr(
              leftType.type,
            )} to ${baseTypeToDisplayStr(rightType.type)}`,
          );
        }

        const canBeOrdered = HAS_ORDER[leftType.type];
        if (!canBeOrdered) {
          this.addTypeError(
            node,
            `Cannot compare ${baseTypeToDisplayStr(
              leftType.type,
            )} to ${baseTypeToDisplayStr(rightType.type)}`,
          );
        }

        return {
          type: BaseCalcType.BOOLEAN,
          kind: unifiedKind,
          containsAggregate,
        };
      }
      default:
        assertNever(node.op, node.op);
    }
  }

  private getTypeAndCheckFunction(
    node: ExtractBranchFromUnion<CalcParseNodeOrError, "type", "function">,
  ): CalcType {
    const argTypes = node.args.map((arg) => this.getTypeAndCheck(arg));

    const functionOverloads = getCalcFunctionInfo(node.name);
    if (functionOverloads == null || functionOverloads.length === 0) {
      this.addTypeError(node, `Unknown function ${node.name}`);
      return {
        type: BaseCalcType.UNKNOWN,
        kind: CalcKind.ANY,
      };
    }

    const argsContainsAggregates = argTypes.some((t) => t.containsAggregate);
    const isAggregate = isHqlAggregationFunctionName(node.name);
    if (isAggregate && this.aggregationMode === "disabled") {
      this.addTypeError(
        node,
        `Aggregation functions can't be used in explorations`,
      );
      return {
        type: BaseCalcType.UNKNOWN,
        kind: CalcKind.ANY,
      };
    }
    if (isAggregate && argsContainsAggregates) {
      this.addTypeError(node, `Cannot nest aggregation functions`);
      return {
        type: BaseCalcType.UNKNOWN,
        kind: CalcKind.ANY,
      };
    }
    if (
      this.disableHqlNextOnlyFunctions &&
      isHqlNextOnlyFunctionName(node.name)
    ) {
      this.addTypeError(node, `${node.name} can only be used in Explore cells`);
      return {
        type: BaseCalcType.UNKNOWN,
        kind: CalcKind.ANY,
      };
    }

    const typeErrors: [CalcParseNodeOrError, string][] = [];
    for (const functionOverload of functionOverloads) {
      const result = this.evaluateFunctionOverload(
        node,
        argTypes,
        functionOverload,
      );
      if (Array.isArray(result)) typeErrors.push(result);
      else
        return {
          ...result,
          containsAggregate: isAggregate || argsContainsAggregates,
        };
    }

    // for now, only specify the first type error, instead of potentially
    // confusing the user with showing multiple overloads
    this.addTypeError(...typeErrors[0]!);
    return { type: BaseCalcType.UNKNOWN, kind: CalcKind.ANY };
  }

  private evaluateFunctionOverload(
    node: ExtractBranchFromUnion<CalcParseNodeOrError, "type", "function">,
    argTypes: CalcType[],
    functionInfo: CalcFunctionInfo,
  ): CalcType | [CalcParseNodeOrError, string] {
    // Assume zero or more variadic parameters at the end of the signature. We will cycle through the types
    // when type checking
    const variadicTypes = functionInfo.parameters
      .filter((p) => p.variadic)
      .map((p) => ({ type: p.type, kind: p.kind }));

    const getParamTypeForPosition = (position: number) => {
      const numNonVariadicParams =
        functionInfo.parameters.length - variadicTypes.length;

      if (position <= numNonVariadicParams - 1) {
        const param = functionInfo.parameters[position];
        return { type: param?.type, kind: param?.kind };
      } else if (position === argTypes.length - 1) {
        // If last argument, use last variadic type (This handles the behavior of SWITCH)
        const param = last(variadicTypes);
        return { type: param?.type, kind: param?.kind };
      } else {
        // Cycle through variadic types
        const variadicIndex =
          (position - numNonVariadicParams) % variadicTypes.length;
        return variadicTypes[variadicIndex];
      }
    };

    const argsAndParams = zip(node.args, argTypes, functionInfo.parameters);

    // Collect the types of arguments that are bound to generic parameters
    const genericTypesT: BaseCalcType[] = [];
    const genericTypesU: BaseCalcType[] = [];
    const genericArgPositionsT: number[] = [];
    const genericArgPositionsU: number[] = [];
    for (const [position, argType] of argTypes.entries()) {
      if (argType != null && argType.type !== BaseCalcType.NULL) {
        const paramType = getParamTypeForPosition(position);
        if (paramType?.type === "T") {
          genericTypesT.push(argType.type);
          genericArgPositionsT.push(position + 1);
        } else if (paramType?.type === "U") {
          genericTypesU.push(argType.type);
          genericArgPositionsU.push(position + 1);
        }
      }
    }

    const validGenericT = genericTypesT.every(
      (v) => v === genericTypesT[0] || v === BaseCalcType.NULL,
    );
    if (!validGenericT) {
      const genericArgs = genericTypesT.map((v) => baseTypeToDisplayStr(v));
      this.addTypeError(
        node,
        `Arguments ${genericArgPositionsT.join(", ")} of ${node.name},` +
          ` must have the same type. Found ${genericArgs.join(", ")}`,
      );
      return {
        type: BaseCalcType.UNKNOWN,
        kind: CalcKind.ANY,
      };
    }

    const validGenericU = genericTypesU.every(
      (v) => v === genericTypesU[0] || v === BaseCalcType.NULL,
    );
    if (!validGenericU) {
      const genericArgs = genericTypesU.map((v) => baseTypeToDisplayStr(v));
      return [
        node,
        `Arguments ${genericArgPositionsU.join(", ")} of ${node.name},` +
          ` must have the same type. Found ${genericArgs.join(", ")}`,
      ];
    }

    // eslint-disable-next-line prefer-const -- intentionally using let for `param`
    for (let [position, [arg, argType, param]] of argsAndParams.entries()) {
      if (arg == null && param != null) {
        return [
          node,
          `Not enough arguments for function ${node.name},` +
            ` only ${node.args.length} found and ${functionInfo.parameters.length} expected`,
        ];
      }

      // Should be impossible to hit this case, but check for type safety anyway
      if (arg == null || argType == null) continue;

      // Check for too many args in non-variadic case
      if (param == null && variadicTypes.length === 0) {
        return [
          arg,
          `Too many arguments for function ${node.name}` +
            (functionInfo.parameters.length === 0
              ? ", which takes no arguments"
              : `, only ${functionInfo.parameters.length} expected`),
        ];
      }

      // compute type of variadic param
      let paramType: CalcParameterType;
      let paramKind: CalcParameterKind;
      if (param == null) {
        const variadicParamType = getParamTypeForPosition(position);
        if (
          variadicParamType?.type != null &&
          variadicParamType?.kind != null
        ) {
          paramType = variadicParamType.type;
          paramKind = variadicParamType.kind;
        } else {
          // Shouldn't happen, but continue without showing user an error just in case
          continue;
        }
      } else {
        paramType = param.type;
        paramKind = param.kind;
      }

      let expectedType: BaseCalcType;
      if (paramType === "T" && genericTypesT[0] != null) {
        expectedType = genericTypesT[0];
      } else if (paramType === "U" && genericTypesU[0] != null) {
        expectedType = genericTypesU[0];
      } else {
        expectedType = calcParamTypeToCalcType(paramType);
      }
      const expected = {
        kind: calcParamKindToCalcKind(paramKind),
        type: expectedType,
      };

      this.expectType(
        argType,
        expected.type,
        arg,
        `Argument was ${baseTypeToDisplayStr(
          argType.type,
        )} but expected ${baseTypeToDisplayStr(expectedType)}`,
      );

      this.expectKind(
        argType,
        expected.kind,
        arg,
        `Argument was ${kindToDisplayStr(
          argType,
        )} but expected ${kindToDisplayStr(expected)}`,
      );
    }

    let returnType = calcParamTypeToCalcType(functionInfo.returnType);
    if (functionInfo.returnType === "T" && genericTypesT[0] != null) {
      returnType = genericTypesT[0];
    } else if (functionInfo.returnType === "U" && genericTypesU[0] != null) {
      returnType = genericTypesU[0];
    }

    // Compute return kind as unification of argument kinds
    const unifiedKind = unifyKinds(argTypes.map((t) => t.kind));

    if (this.aggregationMode === "groupable") {
      // All or none
      const allAreAggregations = argTypes.every(isAggOrScalar);
      const noneAreAggregations = argTypes.every((t) => !t.containsAggregate);
      if (!allAreAggregations && !noneAreAggregations) {
        this.addTypeError(
          node,
          `All arguments must be aggregations, or none must be`,
        );
        return {
          type: BaseCalcType.UNKNOWN,
          kind: CalcKind.ANY,
        };
      }
      if (argsMixAggFunctionAndReferencedAgg(node, argTypes)) {
        this.addTypeError(
          node,
          `Sorry, we don't currently support mixing aggregation functions, and measures or other calculations with aggregations`,
        );
        return {
          type: BaseCalcType.UNKNOWN,
          kind: CalcKind.ANY,
        };
      }
    }

    return { type: returnType, kind: unifiedKind };
  }
}

const isAggOrScalar = (t: CalcType): boolean => {
  return t.containsAggregate || unifyKinds([t.kind]) === CalcKind.SCALAR;
};

// True if kind1 is a subkind of kind2, which means that kind1 is valid anywhere
// kind2 is required.
function isSubKind(kind1: CalcKind, kind2: CalcKind): boolean {
  return (
    kind1 === kind2 ||
    kind2 === CalcKind.ANY ||
    (kind1 === CalcKind.LITERAL_SCALAR && kind2 === CalcKind.SCALAR) ||
    (kind1 === CalcKind.LITERAL_COLUMN && kind2 === CalcKind.COLUMN)
  );
}

function unifyKinds(kinds: CalcKind[]): CalcKind {
  // Replace literals with scalars (a unified kind is never a literal)
  const nonLiteralKinds = new Set(
    kinds.map((k) => {
      if (k === CalcKind.LITERAL_SCALAR) {
        return CalcKind.SCALAR;
      } else if (k === CalcKind.LITERAL_COLUMN) {
        return CalcKind.COLUMN;
      } else {
        return k;
      }
    }),
  );
  if (nonLiteralKinds.size === 0) {
    // Treat empty set as scalar (e.g. the return value of function with no arguments)
    return CalcKind.SCALAR;
  } else if (nonLiteralKinds.size === 1) {
    // Single kind propagates
    return nonLiteralKinds.values().next().value!; // non-null assetion is fine since we check size
  } else if (
    setEquals(nonLiteralKinds, new Set([CalcKind.SCALAR, CalcKind.COLUMN]))
  ) {
    // Mix of Scalar and Column unifies to column
    // We'll need to update this to accommodate an aggregation kind
    return CalcKind.COLUMN;
  } else {
    // Other combinations (e.g. Scalar and Column) unify to ANY
    return CalcKind.ANY;
  }
}

const areAggAndScalar = (leftType: CalcType, rightType: CalcType): boolean => {
  return (
    !!leftType.containsAggregate &&
    [CalcKind.SCALAR, CalcKind.LITERAL_SCALAR].includes(rightType.kind)
  );
};

const isGroupableBinaryOpValid = (
  leftType: CalcType,
  rightType: CalcType,
): boolean => {
  if (
    areAggAndScalar(leftType, rightType) ||
    areAggAndScalar(rightType, leftType)
  ) {
    return true;
  }
  if (leftType.containsAggregate && rightType.containsAggregate) {
    return true;
  }
  if (!leftType.containsAggregate && !rightType.containsAggregate) {
    return true;
  }
  return false;
};

type LeftOrRightOfBinaryOp = ExtractBranchFromUnion<
  CalcParseNodeOrError,
  "type",
  "binaryOp"
>["left"];

const isAggFunction = (node: LeftOrRightOfBinaryOp): boolean => {
  return node.type === "function" && isHqlAggregationFunctionName(node.name);
};

const isReferencedAgg = (
  node: LeftOrRightOfBinaryOp,
  calcType: CalcType,
): boolean => {
  return (
    node.type === "column" &&
    calcType.kind === CalcKind.COLUMN &&
    !!calcType.containsAggregate
  );
};

const isAggFunctionAndReferencedAgg = (
  left: LeftOrRightOfBinaryOp,
  right: LeftOrRightOfBinaryOp,
  rightType: CalcType,
): boolean => {
  return isAggFunction(left) && isReferencedAgg(right, rightType);
};

/**
 * Temporary hex-sl restriction against "agg_formula_1 + Sum(col)"
 * See https://www.notion.so/hexhq/Agg-calcs-RFC-19f45d6bfe7c80d6911cd5cd540a225c?pvs=4#1a045d6bfe7c809aa96cfad27dcc8dd9
 */
const mixesAggFunctionAndReferencedAgg = (
  left: LeftOrRightOfBinaryOp,
  leftType: CalcType,
  right: LeftOrRightOfBinaryOp,
  rightType: CalcType,
): boolean => {
  return (
    isAggFunctionAndReferencedAgg(left, right, rightType) ||
    isAggFunctionAndReferencedAgg(right, left, leftType)
  );
};

const argsMixAggFunctionAndReferencedAgg = (
  node: ExtractBranchFromUnion<CalcParseNodeOrError, "type", "function">,
  argTypes: CalcType[],
): boolean => {
  const hasAggFunction = node.args.some(isAggFunction);
  const hasReferencedAgg = node.args.some((arg, idx) =>
    isReferencedAgg(arg, argTypes[idx]!),
  );
  return hasAggFunction && hasReferencedAgg;
};

const hqlOpToVerb: Record<HqlBinaryOperator, string> = {
  PLUS: "add",
  MINUS: "subtract",
  MULTIPLY: "multiply",
  DIVIDE: "divide",
  EXPONENT: "exponentiate",
  MODULO: "modulo",
  CONCAT: "concatenate",
  EQUAL: "compare",
  NOTEQUAL: "compare",
  GREATER: "compare",
  GREATEREQUAL: "compare",
  LESS: "compare",
  LESSEQUAL: "compare",
  LOGICALOR: '"or"',
  LOGICALAND: '"and"',
};
